import { useState, useEffect } from 'react';
import { api, getCategories } from '../api';
import { styles } from '../styles';
import { BackIcon } from '../components/Icons';

export default function ManualEntryScreen({ currentUser, onSuccess, onCancel }) {
  const [merchant, setMerchant] = useState('');
  const [amount, setAmount] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [expenseType, setExpenseType] = useState('personal');
  const [categories, setCategories] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  
  // Split state
  const [isSplit, setIsSplit] = useState(false);
  const [splits, setSplits] = useState([{ categoryId: '', amount: '' }]);

  useEffect(() => {
    getCategories()
      .then(setCategories)
      .catch(() => {});
  }, []);

  const splitsTotal = splits.reduce((sum, s) => sum + (parseFloat(s.amount) || 0), 0);
  const grandTotal = parseFloat(amount) || 0;
  const remainder = grandTotal - splitsTotal;

  const addSplit = () => {
    setSplits([...splits, { categoryId: '', amount: '' }]);
  };

  const removeSplit = (index) => {
    if (splits.length > 1) {
      setSplits(splits.filter((_, i) => i !== index));
    }
  };

  const updateSplit = (index, field, value) => {
    const updated = [...splits];
    updated[index][field] = value;
    setSplits(updated);
  };

  const handleSubmit = async () => {
    if (!merchant || !amount || !categoryId) {
      setError('Please fill in merchant, amount, and category');
      return;
    }

    if (isSplit) {
      const invalidSplits = splits.some(s => !s.categoryId || !s.amount);
      if (invalidSplits) {
        setError('Please fill in all split fields');
        return;
      }
      if (splitsTotal >= grandTotal) {
        setError('Splits must be less than the total amount');
        return;
      }
      if (remainder <= 0) {
        setError('Nothing left for primary category');
        return;
      }
    }
  
    setSaving(true);
    setError(null);
  
    try {
      const payload = {
        merchant_name: merchant,
        grand_total: parseFloat(amount),
        category_id: categoryId,
        transaction_date: new Date(date).toISOString(),
        expense_type: expenseType,
      };

      if (isSplit) {
        payload.splits = splits.map(s => ({
          category_id: s.categoryId,
          amount: parseFloat(s.amount),
        }));
      }

      await api.post(`/receipts/manual?user_id=${currentUser}`, payload);
      onSuccess();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const selectedCategory = categories.find(c => c.id === categoryId);

  return (
    <div style={styles.screen}>
      <header style={styles.header}>
        <button style={styles.backBtn} onClick={onCancel}>
          <BackIcon />
        </button>
        <h1 style={styles.headerTitle}>Manual Entry</h1>
        <div style={{ width: 20 }} />
      </header>

      <div style={styles.form}>
        <div style={styles.field}>
          <label style={styles.label}>Merchant</label>
          <input
            style={styles.input}
            type="text"
            placeholder="Where did you spend?"
            value={merchant}
            onChange={(e) => setMerchant(e.target.value)}
          />
        </div>

        <div style={styles.field}>
          <label style={styles.label}>Amount</label>
          <input
            style={styles.input}
            type="number"
            step="0.01"
            placeholder="0.00"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
          />
        </div>

        <div style={styles.field}>
          <label style={styles.label}>Category</label>
          <select
            style={styles.input}
            value={categoryId}
            onChange={(e) => setCategoryId(e.target.value)}
          >
            <option value="">Select category...</option>
            {categories.filter(c => c.parent_id === null && c.budget_type !== 'project').map(c => (
              <option key={c.id} value={c.id}>{c.icon} {c.name}</option>
            ))}
            {categories.filter(c => c.parent_id === null && c.budget_type === 'project').map(proj => {
              const subs = categories.filter(c => c.parent_id === proj.id);
              if (!subs.length) return null;
              return (
                <optgroup key={proj.id} label={`${proj.icon || ''} ${proj.name}`}>
                  {subs.map(c => (
                    <option key={c.id} value={c.id}>{c.icon} {c.name}</option>
                  ))}
                </optgroup>
              );
            })}
          </select>
        </div>

        <div style={styles.field}>
          <label style={styles.label}>Date</label>
          <input
            style={styles.input}
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
          />
        </div>

        <div style={styles.field}>
          <label style={styles.label}>Type</label>
          <select
            style={styles.input}
            value={expenseType}
            onChange={(e) => setExpenseType(e.target.value)}
          >
            <option value="personal">Personal</option>
            <option value="corporate">Corporate</option>
          </select>
        </div>

        {/* Split toggle */}
        <div style={{ ...styles.field, flexDirection: 'row', alignItems: 'center', gap: 10 }}>
          <input
            type="checkbox"
            id="splitToggle"
            checked={isSplit}
            onChange={(e) => setIsSplit(e.target.checked)}
          />
          <label htmlFor="splitToggle" style={{ ...styles.label, marginBottom: 0 }}>
            Split across categories
          </label>
        </div>

        {isSplit && (
          <div style={styles.field}>
            <label style={styles.label}>Split portions</label>
            
            {splits.map((split, idx) => (
              <div key={idx} style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                <select
                  style={{ ...styles.input, flex: 2 }}
                  value={split.categoryId}
                  onChange={(e) => updateSplit(idx, 'categoryId', e.target.value)}
                >
                  <option value="">Category...</option>
                  {categories.filter(c => c.id !== categoryId).map((c) => (
                    <option key={c.id} value={c.id}>{c.icon} {c.name}</option>
                  ))}
                </select>
                <input
                  style={{ ...styles.input, flex: 1 }}
                  type="number"
                  step="0.01"
                  placeholder="0.00"
                  value={split.amount}
                  onChange={(e) => updateSplit(idx, 'amount', e.target.value)}
                />
                {splits.length > 1 && (
                  <button
                    style={{ ...styles.backBtn, color: '#ef4444' }}
                    onClick={() => removeSplit(idx)}
                  >
                    ✕
                  </button>
                )}
              </div>
            ))}
            
            <button
              style={{ ...styles.secondaryBtn, marginTop: 4 }}
              onClick={addSplit}
            >
              + Add another split
            </button>

            {/* Show remainder going to primary category */}
            {selectedCategory && amount && (
              <div style={{ 
                marginTop: 12, 
                padding: 12, 
                backgroundColor: '#f0fdf4', 
                borderRadius: 8,
                border: '1px solid #bbf7d0'
              }}>
                <span style={{ color: '#166534', fontSize: 14 }}>
                  {selectedCategory.icon} {selectedCategory.name}: <strong>${remainder.toFixed(2)}</strong> (remainder)
                </span>
              </div>
            )}
          </div>
        )}

        {error && <p style={styles.error}>{error}</p>}

        <button
          style={{ ...styles.primaryBtn, marginTop: 20, opacity: saving ? 0.6 : 1 }}
          onClick={handleSubmit}
          disabled={saving}
        >
          {saving ? 'Saving...' : 'Save Expense'}
        </button>
      </div>
    </div>
  );
}